// ===== STATE MANAGEMENT =====
const AutocompleteState = {
  query: '',
  rid: null,
  itemLimit: null,
  activeInput: null,
  latestRequestId: 0,
  isOpen: false,
  hasRenderedOnce: false, // Track if Findify has rendered at least once

  init() {
    if (typeof findify !== 'undefined' && findify?.autocomplete?.state?.meta) {
      const meta = findify.autocomplete.state.meta;
      this.rid = meta.rid;
      this.query = meta.q;
      this.itemLimit = meta.item_limit;
    }
  },

  setQuery(query) {
    this.query = query;
  },

  setActiveInput(input) {
    this.activeInput = input;
  },

  getNextRequestId() {
    return ++this.latestRequestId;
  }
};

// ===== API MODULE =====
const SearchAPI = {
  msDelay: 250,
  debounceTimer: null,

  debounce(fn, ms) {
    return {
      call: (...args) => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => fn.apply(this, args), ms || 0);
      },
      cancel: () => {
        clearTimeout(this.debounceTimer);
      }
    };
  },

  async fetchAutocomplete(query, requestId) {
    if (typeof findify === 'undefined' || !findify?.autocomplete?.api) {
      console.warn('Findify autocomplete not available');
      return null;
    }

    try {
      const response = await findify.autocomplete.api(query);

      // Ignore stale responses
      if (requestId !== AutocompleteState.latestRequestId) return null;

      AutocompleteState.rid = response.meta?.rid || AutocompleteState.rid;
      await findify.autocomplete.render(response, findify.autocomplete.liquid);

      // Mark that we've rendered at least once
      AutocompleteState.hasRenderedOnce = true;

      // Ensure autocomplete stays visible and positioned correctly after render
      const autocomplete = document.querySelector('.findify-autocomplete');
      if (autocomplete && AutocompleteState.isOpen) {
        autocomplete.classList.remove('findify-hidden');

        // Re-apply positioning after Findify render (which might reset inline styles)
        if (autocomplete.classList.contains('findify-suggestions-horizontal')) {
          PositionManager.setHorizontalAutocompletePosition();
        }
      }

      // Show content after Findify render (fixes flash of stale content on first load)
      const autocompleteContent = autocomplete?.querySelector('.findify-autocomplete-content');
      if (autocompleteContent) {
        autocompleteContent.style.visibility = 'visible';
        autocompleteContent.style.opacity = '1';
      }

      // Initialize Stamped ratings
      this.initializeRatings(query);

      return response;
    } catch (err) {
      console.log('findify autocomplete api error', err);
      return null;
    }
  },

  initializeRatings(query) {
    if (typeof StampedFn === 'undefined' || typeof StampedFn.loadBadges !== 'function') return;

    const ratingContainers = document.querySelectorAll('.findify-autocomplete-product-rating .stamped-rating');
    if (ratingContainers.length === 0) return;

    const shouldShowSkeleton = query && query.trim() !== '';

    if (shouldShowSkeleton) {
      ratingContainers.forEach(el => el.classList.add('loading'));
    }

    setTimeout(() => {
      StampedFn.loadBadges();
      setTimeout(() => {
        ratingContainers.forEach(el => el.classList.remove('loading'));
      }, shouldShowSkeleton ? 1000 : 0);
    }, 100);
  },

  sendAnalyticsEvent(eventType, data) {
    if (typeof findify === 'undefined') return;
    try {
      findify.core.analytics.sendEvent(eventType, data);
    } catch (error) {
      console.log('analytics error', error);
    }
  }
};

// (Recent searches feature removed for performance)

// ===== POSITION MANAGER =====
const PositionManager = {
  ANNOUNCEMENT_RIBBON_HEIGHT: 60,
  USP_BANNER_HEIGHT: 52, // USP banner height 40px + margin-bottom of 12px
  NAVBAR_HEIGHT: 54, // Base navbar height (fallback)
  GAP: 10, // Gap above searchbar

  getHeaderNewOffset() {
    // If new header is fixed/compact, reserve its height
    const body = document.body;
    if (!body) return 0;
    const isFixed = body.classList?.contains('header-new-fixed');
    if (!isFixed) return 0;
    // Try CSS var set by header-new.js
    const varVal = getComputedStyle(body).getPropertyValue('--header-new-offset');
    const fromVar = varVal ? parseInt(varVal, 10) : 0;
    if (fromVar > 0) return fromVar;
    // Fallback: measure element
    const header = document.getElementById('header-new');
    return header ? header.offsetHeight : 0;
  },

  getBottom(el) {
    return el.getBoundingClientRect().bottom;
  },

  setAutocompletePosition(input) {
    const autocomplete = document.querySelector('.findify-autocomplete');
    if (!autocomplete) return;

    // Use viewport position + scroll to support absolute-positioned containers
    const top = window.scrollY + this.getBottom(input) + 2;
    autocomplete.style.top = `${top}px`;
  },

  calculateTopOffset() {
    let offset = 0;

    // Check if announcement ribbon exists and is visible
    const announcementRibbon = document.querySelector('.announcement-ribbon-wrapper');
    if (announcementRibbon && announcementRibbon.offsetHeight > 0 && announcementRibbon.style.display !== 'none') {
      offset += this.ANNOUNCEMENT_RIBBON_HEIGHT;
    }

    // Check if USP banner exists and is visible
    const uspBannerInner = document.querySelector('.usp-banner-inner');
    if (uspBannerInner && uspBannerInner.offsetHeight > 0) {
      offset += this.USP_BANNER_HEIGHT;
    }

    // Include fixed header-new offset if present
    offset += this.getHeaderNewOffset();

    // Add gap
    offset += this.GAP;

    return offset;
  },

  setModalSearchBarPosition() {
    const modalSearchBar = document.querySelector('.findify-standalone-search-bar');
    if (!modalSearchBar) return;

    // On mobile, remove inline top style (CSS will handle it)
    if (window.innerWidth <= 768) {
      modalSearchBar.style.top = '';
      return;
    }

    // Desktop: set calculated position
    const topOffset = this.calculateTopOffset();
    modalSearchBar.style.top = `${topOffset + 6}px`;
  },

  setHorizontalAutocompletePosition() {
    const autocomplete = document.querySelector('.findify-autocomplete.findify-suggestions-horizontal');
    if (!autocomplete) return;

    // On mobile, remove inline top style (CSS will handle it)
    if (window.innerWidth <= 768) {
      autocomplete.style.top = '';
      return;
    }

    // Desktop: set calculated position
    const topOffset = this.calculateTopOffset();
    // Prefer dynamic header height when compact; otherwise fallback to NAVBAR_HEIGHT
    const dynamicHeader = this.getHeaderNewOffset();
    const headerHeight = dynamicHeader > 0 ? dynamicHeader : this.NAVBAR_HEIGHT;
    const autocompleteTop = topOffset + headerHeight + 2; // 2px gap below modal search bar

    autocomplete.style.top = `${autocompleteTop}px`;
  },

  setFullscreenHeight() {
    if (!AutocompleteState.itemLimit || AutocompleteState.itemLimit <= 4) return;

    const ac = document.querySelector('.findify-autocomplete');
    if (!ac) return;

    ac.style.height = window.innerWidth <= 768 ? '100%' : '80%';
  }
};

// ===== NAVIGATION MODULE =====
const NavigationHelper = {
  getSearchDestination(query) {
    const root = window.Shopify?.routes?.root || '';
    return `${root}search?q=${encodeURIComponent(query)}`;
  },

  navigate(e, query, rid) {
    const url = this.getSearchDestination(query);
    const openInNewWindow = e && (e.ctrlKey || e.metaKey);
    const redirections = (typeof findify !== 'undefined') ? findify.core.merchantConfig.redirections : null;
    const redirectionURL = redirections && redirections[query] ? redirections[query] : url;

    if (redirections && redirections[query]) {
      SearchAPI.sendAnalyticsEvent('redirect', { rid, suggestion: query });
    }

    if (openInNewWindow) {
      window.open(redirectionURL, '_blank');
    } else {
      window.location.href = redirectionURL;
    }
  },

  handleSearch(e) {
    e.preventDefault();
    e.stopPropagation();
    const query = e.target.value.trim();
    
    this.navigate(e, query, AutocompleteState.rid);
  },

  handleSuggestionClick(e, suggestion) {
    e.preventDefault();
    e.stopPropagation();

    if (typeof findify !== 'undefined') {
      const meta = findify.autocomplete.state.meta;
      SearchAPI.sendAnalyticsEvent('click-suggestion', { rid: meta.rid, suggestion });
      this.navigate(e, suggestion, meta.rid);
    }
  }
};

// ===== MODAL SEARCH BAR TEMPLATE =====
const ModalSearchBarTemplate = {
  html: `
    <form action="/search" class="findify-modal-search-form">
      <div class="findify-modal-back-button">
        <button type="button" class="findify-modal-back-btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M8.21577 17.316L1.02875 10.4752C0.900715 10.3535 0.834637 10.1926 0.834637 10.0317C0.834637 9.87077 0.900721 9.70983 1.02793 9.58737L8.28031 2.68384C8.53803 2.43852 8.95644 2.43892 9.21458 2.68384C9.47272 2.92876 9.47272 3.32636 9.21458 3.57168L3.08975 9.40369L17.7507 9.40369C18.3425 9.40369 19.168 9.4399 19.168 10.0317C19.168 10.6235 18.3425 10.6597 17.7507 10.6597L3.08975 10.6597L9.15003 16.4282C9.40817 16.6731 9.40776 17.0707 9.15003 17.316C8.8923 17.5613 8.47391 17.5613 8.21577 17.316Z" fill="black"/>
          </svg>
        </button>
      </div>
      <div class="findify-modal-search-container">
        <div class="findify-modal-input-container">
          <input type="search" name="q" placeholder="" autocomplete="off" id="findify-standalone-search-input" aria-label="">
        </div>
      </div>
    </form>
  `,

  create(headerInput) {
    const container = document.createElement('div');
    container.className = 'findify-standalone-search-bar findify-hidden';
    container.innerHTML = this.html;

    const modalInput = container.querySelector('#findify-standalone-search-input');
    if (headerInput) {
      modalInput.placeholder = headerInput.placeholder || '';
      modalInput.setAttribute('aria-label', headerInput.getAttribute('aria-label') || '');
      modalInput.value = headerInput.value || '';
    }

    return container;
  }
};

// ===== AUTOCOMPLETE MODAL COMPONENT =====
const AutocompleteModal = {
  element: null,
  modalSearchBar: null,
  overlay: null,
  inputSelector: "input[name='q'], #findify-standalone-search-input",

  init() {
    this.element = document.querySelector('.findify-autocomplete');
    this.overlay = document.querySelector('.findify-autocomplete-overlay');
    // Reposition when header layout changes (compact mode toggled)
    window.addEventListener('header-new:compact-changed', () => {
      if (!AutocompleteState.isOpen) return;
      if (this.isHorizontalTemplate()) {
        PositionManager.setModalSearchBarPosition();
        PositionManager.setHorizontalAutocompletePosition();
      } else if (AutocompleteState.activeInput) {
        PositionManager.setAutocompletePosition(AutocompleteState.activeInput);
      }
    });

    // (Reposition on compact change is handled above; avoid extra listeners to prevent layout thrash)
  },

  isHorizontalTemplate() {
    return this.element?.classList.contains('findify-suggestions-horizontal');
  },

  open(input) {
    if (!this.element) return;

    const wasAlreadyOpen = AutocompleteState.isOpen;

    AutocompleteState.setActiveInput(input);
    AutocompleteState.isOpen = true;
    AutocompleteState.justOpened = true; // Prevent immediate closure

    // Lock body without causing a scroll jump: fix first, then set top
    document.body.classList.add('findify-autocomplete-open');
    const scrollY = window.scrollY;
    document.body.style.top = `-${scrollY}px`;

    // Ensure autocomplete is visible
    this.element.classList.remove('findify-hidden');

    this.showOverlay();
    
    // Clear the justOpened flag after a short delay
    // Longer delay to handle slower API responses
    setTimeout(() => {
      AutocompleteState.justOpened = false;
    }, 3000);

    if (this.isHorizontalTemplate()) {
      this.createOrUpdateModalSearchBar(input, wasAlreadyOpen);
    } else {
      PositionManager.setAutocompletePosition(input);
    }
    
    // Note: Injection is handled by SearchAPI.fetchAutocomplete()
    // No need for secondary trigger here - promise queue prevents duplicates
  },

  close() {
    if (!this.element) return;

    this.element.classList.add('findify-hidden');
    document.body.classList.remove('findify-autocomplete-open');
    AutocompleteState.isOpen = false;

    // Restore scroll position (for mobile position:fixed)
    const scrollY = document.body.style.top;
    document.body.style.top = '';
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }

    this.hideOverlay();
    this.hideModalSearchBar();

    // Return focus to submit button
    AutocompleteState.activeInput?.parentNode?.querySelector('button[type="submit"]')?.focus();
  },

  showOverlay() {
    if (this.overlay) {
      this.overlay.classList.remove('findify-hidden', 'hidden');
    }
  },

  hideOverlay() {
    if (this.overlay) {
      this.overlay.classList.add('findify-hidden', 'hidden');
    }
  },

  createOrUpdateModalSearchBar(input, wasAlreadyOpen) {
    if (!this.modalSearchBar) {
      this.modalSearchBar = ModalSearchBarTemplate.create(input);
      document.body.appendChild(this.modalSearchBar);

      // Attach event listeners to modal input
      const modalInput = this.modalSearchBar.querySelector('#findify-standalone-search-input');
      if (modalInput) {
        modalInput.addEventListener('focus', (e) => {
          // Always open immediately for instant feedback
          if (!AutocompleteState.isOpen) {
            if (e.stopPropagation) e.stopPropagation();
            AutocompleteModal.open(e.target);
          }
          // Load content asynchronously
          AutocompleteLoader.loadAutocomplete(e);
        });
        modalInput.addEventListener('keyup', (e) => AutocompleteLoader.handleInputChange(e));
        // Listen for input event (triggered by native clear button on real mobile devices)
        modalInput.addEventListener('input', (e) => AutocompleteLoader.handleInputChange(e));
        // Listen for search event (triggered by native clear button on some browsers)
        modalInput.addEventListener('search', (e) => AutocompleteLoader.handleInputChange(e));
      }
    } else {
      // Update existing modal input value
      const modalInput = this.modalSearchBar.querySelector('#findify-standalone-search-input');
      if (modalInput) {
        modalInput.value = input.value;
      }
    }

    this.modalSearchBar.classList.remove('findify-hidden');

    // Only update positions if modal wasn't already open (initial open)
    if (!wasAlreadyOpen) {
      setTimeout(() => {
        PositionManager.setModalSearchBarPosition();
        PositionManager.setHorizontalAutocompletePosition();

        // Focus the modal input
        const modalInput = this.modalSearchBar.querySelector('#findify-standalone-search-input');
        if (modalInput) {
          modalInput.focus();
        }
      }, 50);
    }
  },

  hideModalSearchBar() {
    if (this.modalSearchBar) {
      this.modalSearchBar.classList.add('findify-hidden');
    }
  },


  shouldCloseOnOutsideClick(target, input) {
    const standaloneSearchBar = document.querySelector('.findify-standalone-search-bar');
    return !this.element?.contains(target) &&
           !input?.contains(target) &&
           !(standaloneSearchBar?.contains(target));
  }
};

// ===== FOCUS TRAP =====
const FocusTrap = {
  handleTabKey(e) {
    if (e.key !== 'Tab' && e.keyCode !== 9) return;

    const modal = document.querySelector('.findify-autocomplete');
    if (!modal) return;

    const focusableElements = 'a, button, [href], input:not([type="hidden"]), select, textarea, iframe, [tabindex]:not([tabindex="-1"])';
    const focusableContent = modal.querySelectorAll(focusableElements);
    if (!focusableContent.length) return;

    const firstFocusableElement = focusableContent[0];
    const lastFocusableElement = focusableContent[focusableContent.length - 1];

    if (document.activeElement === AutocompleteState.activeInput) {
      firstFocusableElement.focus();
      e.preventDefault();
      return;
    }

    if (e.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        AutocompleteState.activeInput?.focus();
        e.preventDefault();
      }
    } else if (document.activeElement === lastFocusableElement) {
      firstFocusableElement.focus();
      e.preventDefault();
    }
  }
};

// ===== AUTOCOMPLETE LOADER =====
const AutocompleteLoader = {
  debouncedLoad: null,

  init() {
    this.debouncedLoad = SearchAPI.debounce(
      this.loadAutocomplete.bind(this),
      SearchAPI.msDelay
    );
  },

  async loadAutocomplete(event) {
    const hasQueryChanged = !event || event.target.value !== AutocompleteState.query;
    const isFirstOpen = !AutocompleteState.isOpen;

    // Always fetch on first open, even if query hasn't changed
    // This ensures recent sections are injected when opening with empty query
    if (hasQueryChanged || isFirstOpen) {
      AutocompleteState.setQuery(event ? event.target.value : '');
      const requestId = AutocompleteState.getNextRequestId();

      await SearchAPI.fetchAutocomplete(AutocompleteState.query, requestId);

      // Attach suggestion click handlers
      this.attachSuggestionHandlers();
      
      // Open modal after data is ready (for first load)
      if (event && event.target && !AutocompleteState.isOpen) {
        if (event.stopPropagation) event.stopPropagation();
        AutocompleteModal.open(event.target);
      }
    }
  },

  attachSuggestionHandlers() {
    const suggestions = document.querySelectorAll('.findify-autocomplete [data-findify-suggestion]');
    suggestions.forEach(element => {
      // Clone to remove old listeners
      const clone = element.cloneNode(true);
      element.parentNode.replaceChild(clone, element);
    });
  },

  handleInputChange(e) {
    // Handle keyboard events (Enter/Escape)
    if (e.type === 'keyup') {
    if (e.code === 'Enter' || e.key === 'Enter') {
      NavigationHelper.handleSearch(e);
        return;
    } else if (e.code === 'Escape' || e.key === 'Escape') {
      AutocompleteModal.close(e);
        return;
      }
    }
    
    // Handle input changes (typing, clear button, etc.)
    const inputValue = e.target.value;
    
    // If input is empty, immediately load to show recent searches
    // This fixes the issue where native clear button doesn't trigger UI update on mobile
    if (!inputValue || inputValue.trim() === '') {
      this.debouncedLoad.cancel();
      this.loadAutocomplete(e);
    } else {
      // For non-empty queries, use debounced load as usual
      this.debouncedLoad.cancel();
      this.debouncedLoad.call(e);
    }
  }
};

// ===== EVENT DELEGATION =====
const EventDelegation = {
  init() {
    // Global click handler for outside clicks
    document.addEventListener('click', this.handleDocumentClick.bind(this));

    // Overlay click handler
    const overlay = document.querySelector('.findify-autocomplete-overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          AutocompleteModal.close(e);
        }
      });
    }

    // Keyboard handlers
    document.addEventListener('keydown', (e) => {
      if (AutocompleteState.isOpen) {
        FocusTrap.handleTabKey(e);
      }
    });

    // Window resize handler - reposition modal when switching between mobile and desktop
    let lastBreakpoint = window.innerWidth > 768 ? 'desktop' : 'mobile';
    window.addEventListener('resize', () => {
      const currentBreakpoint = window.innerWidth > 768 ? 'desktop' : 'mobile';

      // Only reposition if breakpoint changed and modal is open
      if (currentBreakpoint !== lastBreakpoint && AutocompleteState.isOpen) {
        lastBreakpoint = currentBreakpoint;

        if (AutocompleteModal.isHorizontalTemplate()) {
          // Reposition for new breakpoint
          PositionManager.setModalSearchBarPosition();
          PositionManager.setHorizontalAutocompletePosition();
        }
      }
    });

    // Event delegation for dynamic elements
    document.addEventListener('click', (e) => {
      // Back button (close modal)
      if (e.target.closest('.findify-modal-back-btn')) {
        e.preventDefault();
        e.stopPropagation();
        AutocompleteModal.close();
      }

      // Legacy close button
      if (e.target.closest('.findify-close-autocomplete')) {
        AutocompleteModal.close();
      }

      // Suggestion clicks
      const suggestionEl = e.target.closest('[data-findify-suggestion]');
      if (suggestionEl) {
        const suggestion = suggestionEl.innerText;
        NavigationHelper.handleSuggestionClick(e, suggestion);
      }
    });

    // Prevent modal from closing when clicking inside
    document.addEventListener('click', (e) => {
      if (e.target.closest('.findify-standalone-search-bar') ||
          e.target.closest('#findify-standalone-search-input')) {
        e.stopPropagation();
      }
    });

    // Input synchronization for modal and header inputs
    document.addEventListener('input', (e) => {
      if (e.target.id === 'findify-standalone-search-input') {
        const headerInput = document.querySelector("input[name='q']");
        if (headerInput) {
          headerInput.value = e.target.value;
        }
      }
    });
  },

  handleDocumentClick(e) {
    if (!AutocompleteState.isOpen) return;
    
    // Don't close if modal just opened (prevents opening click from closing it)
    if (AutocompleteState.justOpened) return;

    const input = AutocompleteState.activeInput;
    if (AutocompleteModal.shouldCloseOnOutsideClick(e.target, input)) {
      AutocompleteModal.close(e);
    }
  }
};

// ===== INITIALIZATION =====
let initRetryCount = 0;
const MAX_INIT_RETRIES = 50; // 5 seconds max (50 * 100ms)

const initFindifyAutocompleteEvents = () => {
  // Check if Findify is available
  if (typeof findify === 'undefined' || !findify?.autocomplete) {
    initRetryCount++;
    if (initRetryCount < MAX_INIT_RETRIES) {
      // Retry after a short delay
      setTimeout(initFindifyAutocompleteEvents, 100);
    } else {
      console.error('Findify autocomplete failed to load after', MAX_INIT_RETRIES * 100, 'ms');
    }
    return;
  }

  // Initialize state
  AutocompleteState.init();

  // Initialize modules
  AutocompleteModal.init();
  AutocompleteLoader.init();
  EventDelegation.init();

  // Do not hide content initially; open modal immediately and render asynchronously

  // Set fullscreen height
  PositionManager.setFullscreenHeight();

  // Attach input listeners
  const inputs = document.querySelectorAll("input[name='q']");
  inputs.forEach(input => {
    input.addEventListener('focus', (e) => {
      // Always open immediately for instant feedback
      if (!AutocompleteState.isOpen) {
        if (e.stopPropagation) e.stopPropagation();
        AutocompleteModal.open(e.target);
      }
      // Load content asynchronously
      AutocompleteLoader.loadAutocomplete(e);
    });
    input.addEventListener('keyup', (e) => AutocompleteLoader.handleInputChange(e));
    // Listen for input event (triggered by native clear button on real mobile devices)
    input.addEventListener('input', (e) => AutocompleteLoader.handleInputChange(e));
    // Listen for search event (triggered by native clear button on some browsers)
    input.addEventListener('search', (e) => AutocompleteLoader.handleInputChange(e));
  });

  // Attach suggestion handlers for initial render
  AutocompleteLoader.attachSuggestionHandlers();
};

// Initialize when script loads
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFindifyAutocompleteEvents);
  } else {
    initFindifyAutocompleteEvents();
  }
}
